import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAddresses } from "../hooks/use-addresses";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

export const DeleteAddressDialog: React.FC = () => {
  const { t } = useTranslation();
  const {
    isDeleteAddressDialogOpen,
    closeDeleteAddressDialog,
    deletingAddress,
    deleteAddress,
    isDeleting,
  } = useAddresses();

  if (!deletingAddress) {
    return null;
  }

  return (
    <Dialog
      open={isDeleteAddressDialogOpen}
      onOpenChange={closeDeleteAddressDialog}
    >
      <DialogContent
        open={isDeleteAddressDialogOpen}
        className="max-w-md p-6 rounded-[20px] bg-white dark:bg-gray-900"
      >
        {/* Header - Title and Close Button */}
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-[20px] font-semibold leading-[28px] text-[#121926] dark:text-gray-100">
              {t("addresses.deleteAddress")}
            </h2>
            <button
              onClick={closeDeleteAddressDialog}
              className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              disabled={isDeleting}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Confirmation Message */}
          <p className="text-[14px] font-normal leading-[22px] text-[#364152] dark:text-gray-400">
            {t("addresses.deleteAddressConfirmation")}
          </p>

          {/* Address Details Card - Figma Design */}
          <div className="bg-white dark:bg-gray-800 border border-[#e3e8ef] dark:border-gray-700 rounded-[12px] p-3 flex flex-col gap-2">
            {/* Address Label */}
            <div className="flex items-center">
              <p className="text-[14px] font-semibold leading-[20px] text-[#121926] dark:text-gray-100">
                {deletingAddress.label}
              </p>
            </div>

            {/* Address Lines */}
            <div className="flex flex-col gap-0.5 text-[14px] font-normal leading-[20px] text-[#121926] dark:text-gray-300">
              <p>{deletingAddress.addressLine1}</p>
              {deletingAddress.addressLine2 && <p>{deletingAddress.addressLine2}</p>}
              <p>
                {deletingAddress.city}, {deletingAddress.state}{" "}
                {deletingAddress.postalCode}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons - Figma Design */}
        <div className="flex gap-4 mt-6">
          <Button
            variant="outline"
            onClick={closeDeleteAddressDialog}
            disabled={isDeleting}
            className="flex-1 bg-white dark:bg-gray-800 border border-[#e3e8ef] dark:border-gray-700 text-[#414651] dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-[8px] px-[14px] py-[10px]"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={deleteAddress}
            disabled={isDeleting}
            data-dialog-default-action="true"
            autoFocus
            className="flex-1 bg-[#f04438] hover:bg-[#d92d20] dark:bg-[#f04438] dark:hover:bg-[#d92d20] text-white rounded-[10px] px-4 py-[10px] font-semibold text-[14px] leading-[20px]"
          >
            {isDeleting ? t("common.deleting") : t("common.delete")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

